import { Skeleton, SvgIcon } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { SvgIconTypeMap } from '@mui/material/SvgIcon';
import { type OrgColorTheme } from '@parkchirp/ui-library';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import React from 'react';

interface NavLinks {
	name: string;
	path: string;
	icon: (OverridableComponent<SvgIconTypeMap<{}, 'svg'>> & { muiName: string }) | string;
	display: boolean | string;
	externalUrl?: boolean;
}

const isOnlyUppercase = (str: string) => /^[A-Z]+$/.test(str);

const paramToSlug = (param: string) => {
	let finalStr: string[] = [];
	if (param.includes(' ')) {
		finalStr = param.split(' ');
	} else if (isOnlyUppercase(param)) {
		finalStr = [param.toLowerCase()];
	} else {
		// handles PascalCaseStringInputs and camelCaseStringInputs
		finalStr = param.split(/(?=[A-Z0-9])/);
	}

	return finalStr.join('-').toLowerCase();
};

interface SidebarProps {
	navLinks: NavLinks[];
	forTenant?: boolean;
	sidebarOpen: boolean;
	theme: OrgColorTheme;
}

interface SidebarNavSkeletonProps {
	sidebarOpen: boolean;
}

const SidebarNavSkeleton = ({ sidebarOpen }: SidebarNavSkeletonProps) => {
	const skeletonItems = Array.from({ length: 10 }, (_, index) => index);
	return skeletonItems.map((skel) => (
		<li key={skel} className={`sidebar-skeleton ${sidebarOpen ? 'sidebar-open' : ''}`}>
			<a href='#' style={{ display: 'flex', alignItems: 'center' }}>
				<span id='sidebar_icon'>
					<Skeleton className='sidebar-skeleton__icon' variant='circular' />
				</span>
				<Skeleton className='sidebar-skeleton__text' width={108} height={25} variant='rounded' />
			</a>
		</li>
	));
};
const SidebarNav = ({ navLinks, forTenant, sidebarOpen, theme }: SidebarProps) => {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const currentRoute = pathname.split('/')[1];

	return (
		<ul id='pca__sidebar-nav'>
			{navLinks.length === 0 ? (
				<SidebarNavSkeleton sidebarOpen={sidebarOpen} />
			) : (
				navLinks.map(
					(link) =>
						link.display && (
							<li key={link.path} className={`${sidebarOpen ? 'sidebar-open' : ''}`}>
								<Link
									href={{
										pathname: link.externalUrl ? link.path : `/${link.path}/`,
										...(forTenant &&
											searchParams && {
												query: Object.fromEntries(searchParams.entries()),
											}),
									}}
									className={`${currentRoute === link.path ? 'active' : ''}`}
									target={link.externalUrl ? '_blank' : '_self'}
									data-cy={`sidebar-nav-${paramToSlug(link.name)}-button`}
									style={{ display: 'flex', alignItems: 'center' }}
								>
									<SvgIcon
										id='sidebar_icon'
										component={link.icon as any}
										data-cy='sidebar-nav-icon'
										color='inherit'
										sx={{
											color: theme.sidebar.color,
											fontSize: sidebarOpen ? '25px' : '35px',
											width: sidebarOpen ? '35px' : 'auto',
										}}
									/>

									<span data-cy='sidebar-link-text' style={{ color: theme.sidebar.color }}>
										{link.name}
									</span>
								</Link>
							</li>
						),
				)
			)}
		</ul>
	);
};

export default SidebarNav;
